import { Link } from "react-router-dom";
import { AdminLayout } from "./Dashboard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Newspaper, Trophy, TrendingUp, ListOrdered, Video, Image, Upload,
    ShieldCheck, ArrowRight,
} from "lucide-react";

const STUDIO_CARDS = [
    { title: "Articles",    href: "/admin/content",     icon: Newspaper,   tone: "text-blue-400 bg-blue-400/10",
      description: "Andika, tafsiri kwa Kiswahili, na submit kwa idhini ya supervisor. Discover-from-Web auto-populates title/body/image." },
    { title: "Match Results", href: "/admin/matches",   icon: Trophy,      tone: "text-yellow-300 bg-yellow-300/10",
      description: "Import fixtures live from ESPN, add scores, upload highlight images/videos, publish results after approval." },
    { title: "Fixtures",    href: "/admin/schedules",   icon: ListOrdered, tone: "text-emerald-300 bg-emerald-300/10",
      description: "Curate the upcoming schedule — pick by team or league chip, mark featured, propagate to homepage." },
    { title: "Predictions", href: "/admin/predictions", icon: TrendingUp,  tone: "text-electric bg-electric/10",
      description: "Multi-AI predictions with sources. Featured predictions unlock behind the Weekly Paywall." },
    { title: "Standings",   href: "/admin/standings",   icon: ListOrdered, tone: "text-purple-400 bg-purple-400/10",
      description: "Sync league tables from ESPN. Auto-featured on the Swahili homepage after approval." },
    { title: "Approvals Queue", href: "/admin/approvals", icon: ShieldCheck, tone: "text-orange-300 bg-orange-300/10",
      description: "Supervisor cockpit — review, edit recipients (add/remove single or bulk), approve to dispatch, or bounce back for revisions." },
];

const Studio = () => {
    return (
        <AdminLayout title="Content Studio">
            <div className="space-y-6" data-testid="studio-page">
                {/* Header */}
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-heading text-3xl font-black text-white flex items-center gap-3">
                            <Upload className="h-7 w-7 text-electric" />
                            Content Studio
                        </h1>
                        <p className="text-sm text-white/50 mt-1 max-w-2xl">
                            Central desk for content managers — create articles, results, fixtures, predictions and standings. All submissions route through the supervisor approval queue before going live.
                        </p>
                    </div>
                    <Badge className="bg-emerald-400/20 text-emerald-300 border-emerald-400/40 border text-[10px] uppercase tracking-wider">
                        <Image className="h-3 w-3 mr-1" /> Media uploads · CDN swappable
                    </Badge>
                </div>

                <Card className="bg-gradient-to-br from-electric/10 to-paper border-electric/30 p-5">
                    <div className="flex items-start gap-3">
                        <ShieldCheck className="h-5 w-5 text-electric mt-0.5" />
                        <div>
                            <h3 className="font-heading font-bold text-white">Approval-first workflow</h3>
                            <p className="text-sm text-white/60 mt-1">
                                Anything you create as a content manager is queued as <span className="text-yellow-300 font-medium">pending_approval</span>. A supervisor reviews the payload, can edit the recipient list, then approves to dispatch or rejects for revisions. Direct publishing is reserved for admins.
                            </p>
                        </div>
                    </div>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {STUDIO_CARDS.map((c) => (
                        <Link
                            key={c.href}
                            to={c.href}
                            className="group"
                            data-testid={`studio-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <Card className="bg-paper border-border p-5 h-full hover:border-electric/50 transition-colors">
                                <div className={`w-11 h-11 rounded-lg ${c.tone} flex items-center justify-center mb-3`}>
                                    <c.icon className="h-5 w-5" />
                                </div>
                                <h3 className="font-heading text-lg font-bold text-white mb-1 flex items-center gap-2">
                                    {c.title}
                                    <ArrowRight className="h-4 w-4 text-white/30 group-hover:text-electric group-hover:translate-x-0.5 transition-all" />
                                </h3>
                                <p className="text-sm text-white/50 leading-relaxed">{c.description}</p>
                            </Card>
                        </Link>
                    ))}
                </div>

                <Card className="bg-paper border-border p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Video className="h-4 w-4 text-electric" />
                        <h2 className="font-heading text-lg font-bold text-white">Media Uploads</h2>
                    </div>
                    <p className="text-sm text-white/60">
                        Image and video uploads are now supported everywhere URLs were required. Files are stored on the pod today (via <span className="font-mono text-electric">/uploads</span>) — the API surface is CDN-ready, so as soon as you drop in Bunny credentials we&apos;ll swap to CDN URLs with zero UI change.
                    </p>
                </Card>
            </div>
        </AdminLayout>
    );
};

export default Studio;
